import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Activity as ActivityIcon,
  Building2,
  Clapperboard,
  FileSignature,
  FolderOpen,
  Handshake,
  LayoutDashboard,
  MessageSquare,
  Search,
  Settings,
  ShieldCheck,
  Sun,
  User,
  Users,
  type LucideIcon,
  Moon,
} from "lucide-react";
import { Config, Deals } from "@/api/endpoints";
import type { Deal } from "@/api/types";
import { eur } from "@/lib/format";
import { stageColor } from "@/lib/colors";
import { useResolvedTheme } from "@/lib/theme";
import { useAuthStore } from "@/store/auth";
import { useUIStore } from "@/store/ui";

/** A destination, or (with `run`) an action such as switching theme. */
type NavItem = { kind: "nav"; label: string; to: string; icon: LucideIcon; run?: () => void };
type DealItem = { kind: "deal"; deal: Deal };
type OwnerItem = { kind: "owner"; id: string; name: string };
type Item = NavItem | DealItem | OwnerItem;

const NAV_ITEMS: NavItem[] = [
  { kind: "nav", label: "Go to Home", to: "/", icon: MessageSquare },
  { kind: "nav", label: "Go to Today", to: "/today", icon: Sun },
  { kind: "nav", label: "Go to Deals", to: "/deals", icon: Handshake },
  { kind: "nav", label: "Go to Accounts", to: "/accounts", icon: Building2 },
  { kind: "nav", label: "Go to Contacts", to: "/contacts", icon: Users },
  { kind: "nav", label: "Go to Library", to: "/library", icon: FolderOpen },
  { kind: "nav", label: "Go to Demos", to: "/demos", icon: Clapperboard },
  { kind: "nav", label: "Go to Contracts", to: "/contracts", icon: FileSignature },
  { kind: "nav", label: "Go to Activity", to: "/activity", icon: ActivityIcon },
  { kind: "nav", label: "Go to Settings", to: "/settings", icon: Settings },
];

// Admin-only destinations — appended for admins, hidden for everyone else.
const ADMIN_NAV_ITEMS: NavItem[] = [
  { kind: "nav", label: "Go to Dashboard", to: "/dashboard", icon: LayoutDashboard },
  { kind: "nav", label: "Go to Admin", to: "/admin", icon: ShieldCheck },
];

/** Returns true when the focused element is something a typist is interacting with. */
function isTypingTarget(el: EventTarget | null): boolean {
  if (!(el instanceof HTMLElement)) return false;
  const tag = el.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
  if (el.isContentEditable) return true;
  return false;
}

export default function CommandPalette() {
  const { cmdkOpen, closeCmdK, toggleCmdK, setOwnerUserId, openDeal, setTheme } = useUIStore();
  const dark = useResolvedTheme() === "dark";
  const isAdmin = useAuthStore((s) => s.user?.role === "admin");
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const lastFocusedRef = useRef<HTMLElement | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const dealsQuery = useQuery({
    queryKey: ["deals-cmdk"],
    queryFn: () => Deals.list({}),
    enabled: cmdkOpen,
    staleTime: 60_000,
  });
  const ownersQuery = useQuery({
    queryKey: ["owners"],
    queryFn: () => Config.listOwners(),
    enabled: cmdkOpen,
    staleTime: 5 * 60_000,
  });

  // Global ⌘K / Ctrl-K listener — opens or closes, never preventDefault while typing
  // unless the user is explicitly hitting the modifier combo.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) {
        // ⌘K is always ours — preventDefault even when an input is focused.
        e.preventDefault();
        toggleCmdK();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [toggleCmdK]);

  // On open: remember last focus, reset state, focus input.
  // On close: restore previous focus, release body-scroll lock.
  useEffect(() => {
    if (cmdkOpen) {
      lastFocusedRef.current = document.activeElement as HTMLElement | null;
      setQ("");
      setActive(0);
      // Lock body scroll while modal is up.
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      // Focus on next frame so the cmdkIn animation has started.
      requestAnimationFrame(() => inputRef.current?.focus());
      return () => {
        document.body.style.overflow = prev;
        const back = lastFocusedRef.current;
        if (back && typeof back.focus === "function") {
          try {
            back.focus();
          } catch {
            /* ignore */
          }
        }
      };
    }
    return undefined;
  }, [cmdkOpen]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    const themeItem: NavItem = {
      kind: "nav",
      label: dark ? "Switch to light theme" : "Switch to dark theme",
      to: "",
      icon: dark ? Sun : Moon,
      run: () => setTheme(dark ? "light" : "dark"),
    };
    const navSource = [...(isAdmin ? [...NAV_ITEMS, ...ADMIN_NAV_ITEMS] : NAV_ITEMS), themeItem];
    const navMatches: NavItem[] = navSource.filter(
      (n) => !term || n.label.toLowerCase().includes(term),
    );
    const dealMatches: DealItem[] = (dealsQuery.data ?? [])
      .filter((d) => {
        if (!term) return true;
        return (
          (d.lead || "").toLowerCase().includes(term) ||
          (d.primary_owner || "").toLowerCase().includes(term) ||
          (d.stage || "").toLowerCase().includes(term)
        );
      })
      .slice(0, 8)
      .map<DealItem>((d) => ({ kind: "deal", deal: d }));
    const ownerMatches: OwnerItem[] = (ownersQuery.data ?? [])
      .filter((o) => !term || o.name.toLowerCase().includes(term))
      .slice(0, 5)
      .map<OwnerItem>((o) => ({ kind: "owner", id: o.id, name: o.name }));
    return { navMatches, dealMatches, ownerMatches };
  }, [q, dealsQuery.data, ownersQuery.data, isAdmin, dark, setTheme]);

  const flat: Item[] = useMemo(
    () => [...filtered.navMatches, ...filtered.dealMatches, ...filtered.ownerMatches],
    [filtered],
  );

  // Reset selection when result set shrinks below it.
  useEffect(() => {
    if (active >= flat.length) setActive(0);
  }, [flat.length, active]);

  if (!cmdkOpen) return null;

  const runItem = (item: Item) => {
    if (item.kind === "nav") {
      if (item.run) item.run();
      else navigate(item.to);
    } else if (item.kind === "deal") {
      openDeal(item.deal.id);
    } else {
      // Show that person's activity across their deals.
      setOwnerUserId(item.id);
      navigate("/activity");
    }
    closeCmdK();
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      e.stopPropagation();
      closeCmdK();
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, Math.max(0, flat.length - 1)));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(0, a - 1));
    } else if (e.key === "Tab") {
      // Trap focus inside the palette.
      e.preventDefault();
      inputRef.current?.focus();
    } else if (e.key === "Enter") {
      const item = flat[active];
      if (item) runItem(item);
    }
  };

  const renderItem = (item: Item, idx: number) => {
    const selected = idx === active;
    if (item.kind === "nav") {
      const Icon = item.icon;
      return (
        <div
          key={`nav-${idx}`}
          className="cmdk-item"
          data-selected={selected ? "true" : "false"}
          onMouseEnter={() => setActive(idx)}
          onClick={() => runItem(item)}
        >
          <Icon className="w-[15px] h-[15px] text-mut-300" />
          <span>{item.label}</span>
          <span className="meta">↵</span>
        </div>
      );
    }
    if (item.kind === "deal") {
      const color = stageColor(item.deal.stage);
      return (
        <div
          key={`deal-${item.deal.id}`}
          className="cmdk-item"
          data-selected={selected ? "true" : "false"}
          onMouseEnter={() => setActive(idx)}
          onClick={() => runItem(item)}
        >
          <span className="stage-dot" style={{ background: color, color }} />
          <span className="truncate flex-1">{item.deal.lead}</span>
          <span className="meta">
            {item.deal.stage} · {eur(item.deal.arr, { short: true })}
          </span>
        </div>
      );
    }
    return (
      <div
        key={`owner-${item.name}`}
        className="cmdk-item"
        data-selected={selected ? "true" : "false"}
        onMouseEnter={() => setActive(idx)}
        onClick={() => runItem(item)}
      >
        <User className="w-[15px] h-[15px] text-mut-300" />
        <span>{item.name}</span>
        <span className="meta">their activity</span>
      </div>
    );
  };

  // Pre-compute each section's starting index into `flat` so that an item's
  // visible position matches its keyboard-navigation index. This avoids the
  // fragile "cursor++ during JSX render" pattern.
  const rawSections: { title: string; items: Item[] }[] = [
    { title: "Navigation", items: filtered.navMatches as Item[] },
    { title: "Deals", items: filtered.dealMatches as Item[] },
    { title: "People", items: filtered.ownerMatches as Item[] },
  ];
  let runningIndex = 0;
  const sections = rawSections.map((s) => {
    const start = runningIndex;
    runningIndex += s.items.length;
    return { ...s, start };
  });

  const empty = flat.length === 0;

  const overlay = (
    <>
      <div className="cmdk-backdrop" onClick={closeCmdK} />
      <div
        ref={panelRef}
        className="cmdk"
        role="dialog"
        aria-modal="true"
        aria-label="Command palette"
      >
        <div className="flex items-center gap-2 px-4 border-b border-fg/[0.06]">
          <Search className="w-4 h-4 text-mut-300 shrink-0" />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Search deals, owners, or jump to a page…"
            className="cmdk-input"
            style={{ border: "none", padding: "1rem 0" }}
            aria-label="Command palette search"
          />
          <span className="kbd">ESC</span>
        </div>
        <div className="cmdk-list">
          {empty ? (
            <div className="py-8 px-4 text-center text-mut-300 text-md">
              No matches for <b className="text-mut-100">"{q}"</b>
            </div>
          ) : (
            sections.map(({ title, items, start }) =>
              items.length === 0 ? null : (
                <div key={title}>
                  <div className="cmdk-section">{title}</div>
                  {items.map((item, i) => renderItem(item, start + i))}
                </div>
              ),
            )
          )}
        </div>
        <div className="flex items-center gap-4 px-4 py-2.5 border-t border-fg/[0.06] text-xs text-mut-300">
          <span>
            <span className="kbd">↵</span> open
          </span>
          <span>
            <span className="kbd">↑</span>
            <span className="kbd">↓</span> navigate
          </span>
          <span className="ml-auto">Search deals and people, or jump to a page</span>
        </div>
      </div>
    </>
  );

  // Portal to <body> so the modal escapes any ancestor with `backdrop-filter`
  // or `transform`, both of which establish a containing block for fixed children.
  if (typeof document === "undefined") return null;
  return createPortal(overlay, document.body);
}

/** Hook: re-export so callers can reference `isTypingTarget` from Layout. */
// eslint-disable-next-line react-refresh/only-export-components -- intentional shared helper; fast-refresh cost is acceptable here.
export { isTypingTarget };
